import { HttpErrorResponse } from '@angular/common/http';

/**
 * 투팩터 인증 블록(`libs/backend/spinoff-platform-two-factor-auth/src`)의 클라이언트 쪽 계약. 플랜:
 * libs/backend/spinoff-platform/plans/active/2026-10-14-two-factor-auth.md
 *
 * 서버는 보호 API에 유효한 인증이 없으면 `428 TWO_FACTOR_AUTH_REQUIRED { allowedMethods }`를 내리고, 이 lib의 인터셉터가 검증
 * 다이얼로그를 열어 인증을 마친 뒤 같은 요청을 한 번 재전송한다. 응답 본문은 `{ statusCode, code, message, ...extra }`다.
 */

export type SpinoffTwoFactorAuthMethod = 'otp' | 'sms_link';

/** 이 클라이언트가 UI를 제공하는 방식. 서버가 더 많은 방식을 허용해도 여기 있는 것만 시작한다(`sms_link`는 후속). */
export const SUPPORTED_SPINOFF_TWO_FACTOR_AUTH_METHODS: readonly SpinoffTwoFactorAuthMethod[] = ['otp'];

/** 서버 응답 본문의 `code`. 상태 코드가 같아도(예: 401, 429) 이 값으로 구분한다. */
export const SPINOFF_TWO_FACTOR_AUTH_ERROR = {
  REQUIRED: 'TWO_FACTOR_AUTH_REQUIRED',
  PHONE_UNVERIFIED: 'TWO_FACTOR_AUTH_PHONE_UNVERIFIED',
  NOT_APPLICABLE: 'TWO_FACTOR_AUTH_NOT_APPLICABLE',
  SESSION_UNIDENTIFIED: 'TWO_FACTOR_AUTH_SESSION_UNIDENTIFIED',
  METHOD_NOT_ALLOWED: 'TWO_FACTOR_AUTH_METHOD_NOT_ALLOWED',
  CHALLENGE_NOT_FOUND: 'TWO_FACTOR_AUTH_CHALLENGE_NOT_FOUND',
  CHALLENGE_EXPIRED: 'TWO_FACTOR_AUTH_CHALLENGE_EXPIRED',
  INVALID_CODE: 'TWO_FACTOR_AUTH_INVALID_CODE',
  TOO_MANY_ATTEMPTS: 'TWO_FACTOR_AUTH_TOO_MANY_ATTEMPTS',
  RATE_LIMITED: 'TWO_FACTOR_AUTH_RATE_LIMITED',
  SEND_FAILED: 'TWO_FACTOR_AUTH_SEND_FAILED',
} as const;

export interface SpinoffTwoFactorAuthChallenge {
  challengeId: string;
  method: SpinoffTwoFactorAuthMethod;
  expiresAt: string;
  /** dry-run 발송 환경(로컬·e2e)에서만 채워진다. */
  dryRunCode?: string;
}

export interface SpinoffTwoFactorAuthVerified {
  method: SpinoffTwoFactorAuthMethod;
  validUntil: string;
}

export interface SpinoffTwoFactorAuthStatus {
  valid: boolean;
  method?: SpinoffTwoFactorAuthMethod;
  validUntil?: string;
}

/** 검증 프롬프트(다이얼로그)가 끝난 결과. */
export type SpinoffTwoFactorAuthPromptResult =
  | { kind: 'verified' }
  | { kind: 'cancelled' }
  /** 인증을 시작조차 못 한 오류(예: 검증된 번호 없음 409) — 호출한 화면이 안내할 수 있게 원래 오류를 그대로 전한다. */
  | { kind: 'error'; error: unknown };

/** 사용자가 인증을 취소했다. 인터셉터가 원래의 428 오류로 되돌려 던지므로 보통 화면까지 올라오지 않는다. */
export class SpinoffTwoFactorAuthCancelledError extends Error {
  constructor() {
    super('투팩터 인증이 취소되었습니다.');
    this.name = 'SpinoffTwoFactorAuthCancelledError';
  }
}

/** 서버 오류 응답 본문의 `code`. 본문이 없거나 형식이 다르면 `null`. */
export function spinoffTwoFactorAuthErrorCode(err: unknown): string | null {
  if (!(err instanceof HttpErrorResponse)) return null;
  const code = (err.error as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : null;
}

/** 보호 API가 투팩터 인증을 요구하는 428인가. */
export function isSpinoffTwoFactorAuthRequired(err: unknown): err is HttpErrorResponse {
  return err instanceof HttpErrorResponse && err.status === 428 && spinoffTwoFactorAuthErrorCode(err) === SPINOFF_TWO_FACTOR_AUTH_ERROR.REQUIRED;
}

/**
 * 계정에 검증된 휴대폰 번호가 없어 인증 코드를 보낼 곳이 없는 409인가. 이 lib는 안내 문구를 정하지 않는다 — 번호 등록·검증 안내는
 * 앱의 몫이므로 앱이 이 함수로 알아보고 자기 화면에 안내를 띄운다.
 */
export function isSpinoffTwoFactorAuthPhoneUnverified(err: unknown): err is HttpErrorResponse {
  return err instanceof HttpErrorResponse && err.status === 409 && spinoffTwoFactorAuthErrorCode(err) === SPINOFF_TWO_FACTOR_AUTH_ERROR.PHONE_UNVERIFIED;
}

/** 428 응답이 알려준 허용 방식. 형식이 다르면 빈 배열. */
export function spinoffTwoFactorAuthAllowedMethods(err: unknown): SpinoffTwoFactorAuthMethod[] {
  if (!(err instanceof HttpErrorResponse)) return [];
  const methods = (err.error as { allowedMethods?: unknown } | null)?.allowedMethods;
  if (!Array.isArray(methods)) return [];
  return methods.filter((m): m is SpinoffTwoFactorAuthMethod => m === 'otp' || m === 'sms_link');
}
